import React, { useState, useEffect, useRef } from "react";
import { saveOvertimeActivation, saveBankApproval } from "../../services/marcaciones.services";
import {
  Box,
  InputBase,
  IconButton,
  Switch,
  Typography,
  Snackbar,
  Alert,
  useTheme,
  TextField,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { getHorasExtrasView, PostDatosHoras, getOneDatos, PutHorasExtras } from "../../services/horasExtras.services";
import { getBancoPendiente, updateBanco, consumirBancoHoras } from "../../services/banco.services";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ModalHorasExtras from "../../modal/horasExtras/modalHorasExtras";
import { useContext } from 'react';
import { FeriadosContext } from '../../context/FeriadosContext';
import secureStorage from "../../utils/secureStorage";

dayjs.locale('es');


const HorasExtras = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [rowSettings, setRowSettings] = useState({});
  const [bankSettings, setBankSettings] = useState({}); // estado del switch Pago Banco de Hora por fila
  const [bancoData, setBancoData] = useState([]); // datos de banco de horas
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [bankSwitchBusy, setBankSwitchBusy] = useState({});
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [modalJustClosed, setModalJustClosed] = useState(false);
  const { feriados } = useContext(FeriadosContext);
  // Track planes de consumo para poder revertir si el switch se desactiva
  const consumedPlansRef = useRef({});
  const bankSwitchLocksRef = useRef({});
  // Minutos de banco aplicados por fila para mostrar HH:mm
  const [bankAppliedByRow, setBankAppliedByRow] = useState({});

  // Refs to inputs to blur on modal close
  const inputRefs = useRef({});

  useEffect(() => {
  }, [modalOpen]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getHorasExtrasView(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
        setData(response);
        setFilteredData(response);

        const initialSettings = {};
        const initialBankSettings = {};
        response.forEach((row) => {
          initialSettings[row.id_marcacion] = {
            enabled: row.aprobación_horas_extras === 1,
          };
          // Leer aprobacion_bh del backend para pre-marcar el switch de banco
          initialBankSettings[row.id_marcacion] = row.aprobacion_bh === 1;
        });

        setRowSettings(initialSettings);
        setBankSettings(initialBankSettings);
      } catch (error) {
        console.error("Error al obtener datos del backend:", error);
        setSnackbarMessage("Error al cargar los datos.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  // Cargar datos de Banco de Horas
  useEffect(() => {
    const fetchBanco = async () => {
      try {
        const banco = await getBancoPendiente();
        setBancoData(banco || []);
      } catch (e) {
        console.error("Error al obtener banco de horas:", e);
      }
    };
    fetchBanco();
  }, [startDate, endDate]);

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);

    const filtered = data.filter((item) =>
      item.nombre.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const formatMinutesToHHmm = (minutos) => {
    const total = Math.max(0, Math.round(Number(minutos) || 0));
    const horas = Math.floor(total / 60);
    const mins = total % 60;
    return `${horas.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
  };

  const pickNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return null;
  };

  // Helpers de cálculo
  const formatHHmm = (minutos) => {
    const m = Math.max(0, Math.round(minutos || 0));
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}:${mm.toString().padStart(2, '0')}`;
  };
  const isHoliday = (fechaStr) => {
    if (!fechaStr) return false;
    const d = dayjs(fechaStr);
    return feriados.some(f => dayjs(f.fecha).isSame(d, 'day'));
  };

  const calculateRowDesglose = (row) => {
    if (!row) return { tipo: "Sin datos", desglose: {} };

    const fechaBase = row.fecha
      ? dayjs(row.fecha)
      : (row.prohibido ? dayjs(row.prohibido) : dayjs());
    const entradaOriginal = row.prohibido ? dayjs(row.prohibido) : null;
    const salidaOriginal = row.salida ? dayjs(row.salida) : null;

    if (!entradaOriginal || !salidaOriginal || !entradaOriginal.isValid() || !salidaOriginal.isValid()) {
      return { tipo: "Sin datos", desglose: {} };
    }

    const minutosHorario = Number(row.minutos_horario) || 480;
    const minutosAlmuerzo = Number(row.minutos_almuerzo) || 30;
    const horaEntradaHorario = row.hora_entrada_horario;

    let entradaAjustada = entradaOriginal.clone();
    let inicioJornadaRegular = entradaOriginal.clone();
    if (horaEntradaHorario) {
      const [hHorario, mHorario] = horaEntradaHorario.split(":").map((v) => Number(v));
      if (!Number.isNaN(hHorario) && !Number.isNaN(mHorario)) {
        const minutosEntrada = entradaOriginal.hour() * 60 + entradaOriginal.minute();
        const minutosHorarioEntrada = hHorario * 60 + mHorario;
        const horarioOficial = entradaOriginal
          .clone()
          .hour(hHorario)
          .minute(mHorario)
          .second(0)
          .millisecond(0);
        const diferenciaTemprana = minutosHorarioEntrada - minutosEntrada;
        if (diferenciaTemprana > 0 && diferenciaTemprana <= 30) {
          entradaAjustada = entradaAjustada
            .hour(hHorario)
            .minute(mHorario)
            .second(0)
            .millisecond(0);
        }
        if (diferenciaTemprana > 30) {
          inicioJornadaRegular = horarioOficial;
        }
      }
    }

    const minutosTrabajoTotal = Math.max(0, salidaOriginal.diff(entradaAjustada, "minute") - minutosAlmuerzo);

    const esDomingo = fechaBase.day() === 0;
    const esFeriadoDia = Array.isArray(feriados)
      && feriados.some((f) => dayjs(f.fecha).isSame(fechaBase, "day"));

    if (esDomingo || esFeriadoDia) {
      return {
        tipo: "Domingo/Feriado",
        desglose: { domingo: minutosTrabajoTotal },
      };
    }

    let diurnas = 0;
    let nocturnas = 0;
    const sumarMinutoExtra = (instante) => {
      const horaActual = instante.hour();
      if (horaActual >= 20 || horaActual < 6) {
        nocturnas += 1;
      } else {
        diurnas += 1;
      }
    };

    if (inicioJornadaRegular.isAfter(entradaAjustada)) {
      let cursorTemprano = entradaAjustada.clone();
      const finTemprano = salidaOriginal.isBefore(inicioJornadaRegular)
        ? salidaOriginal
        : inicioJornadaRegular;

      while (cursorTemprano.isBefore(finTemprano)) {
        sumarMinutoExtra(cursorTemprano);
        cursorTemprano = cursorTemprano.add(1, "minute");
      }
    }

    const minutosExtrasTempranos = diurnas + nocturnas;
    const minutosTrabajoPosterior = Math.max(0, salidaOriginal.diff(inicioJornadaRegular, "minute") - minutosAlmuerzo);
    const minutosExtrasPosteriores = Math.max(0, minutosTrabajoPosterior - minutosHorario);
    const minutosExtras = minutosExtrasTempranos + minutosExtrasPosteriores;
    if (minutosExtras <= 0) {
      return {
        tipo: "Sin horas extras",
        desglose: { normales: minutosTrabajoTotal },
      };
    }

    const inicioExtra = inicioJornadaRegular.clone().add(minutosHorario + minutosAlmuerzo, "minute");
    let minutosContados = 0;
    let cursor = inicioExtra.clone();

    while (minutosContados < minutosExtrasPosteriores && cursor.isBefore(salidaOriginal)) {
      sumarMinutoExtra(cursor);
      cursor = cursor.add(1, "minute");
      minutosContados += 1;
    }

    return {
      tipo: "Mixta",
      desglose: {
        diurnas,
        nocturnas,
      },
    };
  };

  const getBankEntriesForRow = (row) => {
    // Entradas pendientes del banco con minuto ponderado (domingo/feriado x2)
    const entries = (bancoData || [])
      .filter(b => (b.id_empleado === row.empleado || b.id_usuario === row.id_usuario) && !b.pagar_fecha && b.status_active === 1)
      .map(e => {
        const fechaStr = e.deber_fecha || e.debe_fecha || e.created_at;
        // Consumimos minutos de banco "originales"; el x2 aplica sobre las horas extra, no sobre el banco
        const minutosOriginal = Number((e.minutos) || 0);
        return {
          ...e,
          fecha_ref: fechaStr,
          minutosOriginal,
          minutosPonderados: minutosOriginal,
        };
      })
      .sort((a, b) => dayjs(a.fecha_ref).valueOf() - dayjs(b.fecha_ref).valueOf()); // FIFO por fecha
    return entries;
  };

  const getBankHoursForRow = (row) => {
    // Para visibilidad: devolver horas utilizables del banco en horas (minutos originales / 60)
    return getBankEntriesForRow(row).reduce((acc, e) => acc + (e.minutosOriginal / 60), 0);
  };

  // Calcula un plan de consumo de MINUTOS de banco.
  // Regla: Domingo/Feriado cuesta x2 minutos de banco por cada minuto de HE cubierta.
  // Se consume el banco FIFO y se calcula cobertura por categoría: domingo, nocturnas, diurnas (priorizando más costosas).
  // Devuelve: { plan: [{id, minutos}], usedPondMin, coverage: { domingo, diurnas, nocturnas }, consumidoBanco }
  const computeBankConsumptionPlan = (row, totalOvertimeMin) => {
    const entries = getBankEntriesForRow(row);
    if (!entries.length || totalOvertimeMin <= 0) return { plan: [], usedPondMin: 0 };

    const { domingo, diurnas, nocturnas, total } = getOvertimeMinutesBreakdown(row);
    const costoRequerido = (domingo * 2) + diurnas + nocturnas; // minutos de banco necesarios para cubrir todo
    if (costoRequerido <= 0) return { plan: [], usedPondMin: 0 };

  let remainCost = costoRequerido;
  const plan = [];
  let consumidoBanco = 0;

    for (const e of entries) {
      if (remainCost <= 0) break;
      const capacidad = e.minutosOriginal;
      if (capacidad <= 0) continue;
      const aConsumir = Math.min(capacidad, remainCost);
      plan.push({ id: e.id, minutos: aConsumir });
      consumidoBanco += aConsumir;
      remainCost -= aConsumir;
    }

  // Distribuir cobertura por categoría con costo correcto: Domingo (x2), luego Nocturnas (x1), luego Diurnas (x1)
  // Usamos los valores ya calculados en getOvertimeMinutesBreakdown para evitar recalcular desde columns
  let budget = consumidoBanco; // minutos de banco disponibles
  const totalDom = domingo;
  const totalNoct = nocturnas;
  const totalDiur = diurnas;

    // Domingo: costo 2 por cada 1 minuto cubierto
    const domCost = totalDom * 2;
    let coverDom = 0;
    if (budget >= domCost) {
      coverDom = totalDom;
      budget -= domCost;
    } else {
      coverDom = budget / 2; // cobertura real en minutos de HE
      budget = 0;
    }

    // Nocturnas: costo 1 a 1
    const coverNoc = Math.min(totalNoct, budget);
    budget -= coverNoc;

    // Diurnas: costo 1 a 1
    const coverDiur = Math.min(totalDiur, budget);
    budget -= coverDiur;

    const coverage = { domingo: coverDom, diurnas: coverDiur, nocturnas: coverNoc };
    const usedPondMin = Math.min(totalOvertimeMin, coverDom + coverDiur + coverNoc);
    return { plan, usedPondMin, coverage, consumidoBanco };
  };

  const getOvertimeMinutesBreakdown = (row) => {
    const desglose = calculateRowDesglose(row);
    const domingo = desglose?.desglose?.domingo || 0;
    const diurnas = desglose?.desglose?.diurnas || 0;
    const nocturnas = desglose?.desglose?.nocturnas || 0;
    const total = domingo + diurnas + nocturnas;
    return { domingo, diurnas, nocturnas, total };
  };

  const computeMontoOvertime = (row, consumoBanco = 0) => {
    const { domingo, diurnas, nocturnas, total } = getOvertimeMinutesBreakdown(row);
    if (total <= 0) return { monto: 0, consumoAplicado: 0 };

    let remDom = domingo, remDiur = diurnas, remNoc = nocturnas;
    let consumoAplicado = 0;
    if (typeof consumoBanco === 'object' && consumoBanco !== null) {
      const cDom = Math.max(0, Math.min(domingo, Number(consumoBanco.domingo || 0)));
      const cDiur = Math.max(0, Math.min(diurnas, Number(consumoBanco.diurnas || 0)));
      const cNoc = Math.max(0, Math.min(nocturnas, Number(consumoBanco.nocturnas || 0)));
      remDom = domingo - cDom;
      remDiur = diurnas - cDiur;
      remNoc = nocturnas - cNoc;
      consumoAplicado = cDom + cDiur + cNoc;
    } else {
      let consumo = Math.max(0, Math.min(Number(consumoBanco) || 0, total));
      if (consumo > 0) {
        // Consumir proporcionalmente del total
        const pDom = domingo / total;
        const pDiur = diurnas / total;
        const pNoc = nocturnas / total;
        let cDom = Math.round(consumo * pDom);
        let cDiur = Math.round(consumo * pDiur);
        let cNoc = consumo - cDom - cDiur; // resto
        remDom = Math.max(0, domingo - cDom);
        remDiur = Math.max(0, diurnas - cDiur);
        remNoc = Math.max(0, nocturnas - cNoc);
        consumoAplicado = consumo;
      }
    }

  const salarioMensual = Number(row.salario_monto || 0);
  const divisor = 30 * 8 * 60; // 30 días * 8 horas * 60 minutos
  const salarioPorMinuto = salarioMensual / divisor;
    const monto = Math.round(
      (salarioPorMinuto * 2.0) * remDom +
      (salarioPorMinuto * 1.5) * remDiur +
      (salarioPorMinuto * 2.6) * remNoc
    );
    return {
      monto,
      consumoAplicado,
      remDom,
      remDiur,
      remNoc,
      salarioMensual,
      salarioPorMinuto,
      divisor,
      rates: { domingo: 2.0, diurnas: 1.5, nocturnas: 2.6 },
      totals: { domingo, diurnas, nocturnas, total }
    };
  };


    const esFeriado = (date) => {
      return feriados.some(feriado => 
        dayjs(feriado.fecha).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
      );
    };


  const handleSwitchChange = (id_marcacion) => async (event) => {
    const isChecked = event.target.checked;
    const currentlyEnabled = rowSettings[id_marcacion]?.enabled || false;

    if (isChecked && !currentlyEnabled) {
      event.preventDefault();
      event.stopPropagation();
      await handleOpenModal(id_marcacion);
      return;
    }

    if (isChecked) {
      return;
    }

    const row = filteredData.find((r) => r.id_marcacion === id_marcacion);
    if (!row) return;

    const totalMonto = 0;

    try {
      await saveOvertimeActivation(id_marcacion, totalMonto, 0);
      await PutHorasExtras(id_marcacion, { status_active: 0 });
      setRowSettings((prev) => ({
        ...prev,
        [id_marcacion]: { enabled: false },
      }));

      setFilteredData((prev) =>
        prev.map((rowItem) =>
          rowItem.id_marcacion === id_marcacion
            ? {
                ...rowItem,
                descripcion_horas_extras: '',
                aprobación_horas_extras: 0,
              }
            : rowItem
        )
      );

      setData((prev) =>
        prev.map((rowItem) =>
          rowItem.id_marcacion === id_marcacion
            ? {
                ...rowItem,
                descripcion_horas_extras: '',
                aprobación_horas_extras: 0,
              }
            : rowItem
        )
      );

      setSnackbarMessage("Removido exitosamente del pago");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating overtime approval:", error);
      setSnackbarMessage("Error al actualizar la aprobación");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Manejar switch de Pago Banco de Hora
  const handleBankSwitchChange = (id_marcacion) => async (event) => {
    if (bankSwitchLocksRef.current[id_marcacion]) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const isChecked = event.target.checked;
    const row = filteredData.find((r) => r.id_marcacion === id_marcacion);
    if (!row) return;

    bankSwitchLocksRef.current[id_marcacion] = true;
    setBankSwitchBusy((prev) => ({ ...prev, [id_marcacion]: true }));

    // Mutuamente excluyente: si activo banco, desactivo overtime
    if (isChecked) {
      setRowSettings((prev) => ({ ...prev, [id_marcacion]: { enabled: false } }));
    }

    const { domingo, diurnas, nocturnas, total } = getOvertimeMinutesBreakdown(row);
    const { plan, coverage, consumidoBanco } = computeBankConsumptionPlan(row, total);

    const coverageMinutesPlan = isChecked
      ? (coverage.domingo + coverage.diurnas + coverage.nocturnas)
      : 0;
    let remainderMinutes = Math.max(0, total - coverageMinutesPlan);
    let appliedMinutesDisplay = 0;
    let appliedMinutesForRow = 0;
    let lastBancoResponse = null;
    const consumed = [];

    try {
      if (isChecked && plan.length > 0) {
        const today = dayjs().format('YYYY-MM-DD');
        for (const p of plan) {
          try {
            const pWithUser = { ...p, id_usuario: row.id_usuario };
            const response = await consumirBancoHoras([pWithUser], 'consumir');
            if (response) {
              lastBancoResponse = response;
            }
            consumed.push(pWithUser);
            appliedMinutesDisplay += p.minutos;
            setBancoData((prev) =>
              prev.map((b) => {
                if (b.id !== p.id) return b;
                const actuales = Number((b.horas ?? b.minutos) || 0);
                const nuevosMin = Math.max(0, actuales - p.minutos);
                return { ...b, horas: nuevosMin, pagar_fecha: nuevosMin === 0 ? today : b.pagar_fecha };
              })
            );
          } catch (e) {
            const entry = (bancoData || []).find((b) => b.id === p.id);
            if (entry) {
              const actuales = Number((entry.horas ?? entry.minutos) || 0);
              if (actuales - p.minutos <= 0) {
                await updateBanco([{ id: p.id, pagar_fecha: today }]);
                setBancoData((prev) =>
                  prev.map((b) =>
                    b.id === p.id ? { ...b, horas: 0, pagar_fecha: today } : b
                  )
                );
                consumed.push({ ...p, id_usuario: row.id_usuario });
                appliedMinutesDisplay += p.minutos;
              }
            }
          }
        }

        if (lastBancoResponse?.success) {
          if (typeof lastBancoResponse.minutosProcesados === 'number') {
            appliedMinutesDisplay = Math.max(0, Number(lastBancoResponse.minutosProcesados));
          }
          if (typeof lastBancoResponse.diferencia === 'number') {
            remainderMinutes = Math.max(0, Number(lastBancoResponse.diferencia));
          }
        } else if ((consumidoBanco || 0) > 0 && appliedMinutesDisplay === 0) {
          appliedMinutesDisplay = consumidoBanco;
        }
      }

      appliedMinutesForRow = appliedMinutesDisplay;

      let totalMonto = 0;
      let calcResBH = null;

      if (isChecked) {
        if (domingo > 0 && diurnas === 0 && nocturnas === 0) {
          const salarioMensual = Number(row.salario_monto || 0);
          const divisor = 30 * 8 * 60;
          const salarioPorMinuto = divisor ? (salarioMensual / divisor) : 0;
          const rateDomingo = 2.0;
          const remDomMin = remainderMinutes;
          totalMonto = Math.round(remDomMin * rateDomingo * salarioPorMinuto);
          calcResBH = {
            remDom: remDomMin,
            remDiur: 0,
            remNoc: 0,
            salarioPorMinuto,
            salarioMensual,
            divisor,
            rates: { domingo: rateDomingo, diurnas: 1.5, nocturnas: 2.6 },
            totals: { domingo, diurnas, nocturnas, total },
          };
        } else {
          const coverageMinutesForCalc = Math.max(0, Math.min(total, total - remainderMinutes));
          calcResBH = computeMontoOvertime(row, coverageMinutesForCalc);
          totalMonto = calcResBH.monto;
          remainderMinutes =
            (calcResBH.remDom || 0) + (calcResBH.remDiur || 0) + (calcResBH.remNoc || 0);
        }
      }

      const hasRemainder = isChecked && remainderMinutes > 0;
      const approval = hasRemainder ? 1 : 0;

      if (isChecked) {
        const wasDomingoOnly = domingo > 0 && diurnas === 0 && nocturnas === 0;
        const costoRequerido = wasDomingoOnly
          ? domingo * 2
          : (domingo * 2) + nocturnas + diurnas;
        const costoConsumido = appliedMinutesForRow || 0;
        const costoRestante = Math.max(0, costoRequerido - costoConsumido);
        // console.groupCollapsed(`[BH] Marcación ${id_marcacion} — Cálculo con Banco`);
        // console.log('Overtime total (min):', { domingo, nocturnas, diurnas, total });
        // console.log('Banco consumido (min):', costoConsumido, 'Costo restante estimado:', costoRestante);
        if (plan.length > 0) {
          // console.log('Plan banco:', plan);
        }
        if (!wasDomingoOnly && calcResBH) {
          // console.log('Remanente por categoría (min):', {
          //   domingo: calcResBH.remDom,
          //   nocturnas: calcResBH.remNoc,
          //   diurnas: calcResBH.remDiur,
          // });
        }
        if (wasDomingoOnly && calcResBH) {
          // console.log('Remanente domingo (min):', calcResBH.remDom);
        }
        if (lastBancoResponse) {
          // console.log('Respuesta consumirBancoHoras:', lastBancoResponse);
        }
        if (calcResBH) {
          // console.log('Cálculo salario/min:', {
          //   salarioMensual: calcResBH.salarioMensual,
          //   divisor: calcResBH.divisor,
          //   salarioPorMinuto: calcResBH.salarioPorMinuto,
          // });
          // console.log('Tarifas por minuto:', {
          //   salarioPorMinuto: calcResBH.salarioPorMinuto,
          //   ...calcResBH.rates,
          // });
        }
        // console.log('Monto asignado (₲):', totalMonto);
        // console.groupEnd();
      }

      await saveOvertimeActivation(id_marcacion, totalMonto, approval);

      if (isChecked && consumed.length > 0) {
        consumedPlansRef.current[id_marcacion] = consumed;
        setBankAppliedByRow((prev) => ({
          ...prev,
          [id_marcacion]: appliedMinutesForRow,
        }));
        try {
          await saveBankApproval(id_marcacion, 1);
        } catch (e) {
          console.error('Error set aprobacion_bh=1:', e);
        }
      } else if (isChecked) {
        consumedPlansRef.current[id_marcacion] = [];
        setBankAppliedByRow((prev) => ({
          ...prev,
          [id_marcacion]: appliedMinutesForRow,
        }));
        if (appliedMinutesForRow > 0) {
          try {
            await saveBankApproval(id_marcacion, 1);
          } catch (e) {
            console.error('Error set aprobacion_bh=1:', e);
          }
        }
      }

      setBankSettings((prev) => ({ ...prev, [id_marcacion]: isChecked }));
      setRowSettings((prev) => ({
        ...prev,
        [id_marcacion]: { enabled: hasRemainder },
      }));

      setFilteredData((prev) =>
        prev.map((r) =>
          r.id_marcacion === id_marcacion
            ? { ...r, descripcion_horas_extras: totalMonto ? totalMonto : '' }
            : r
        )
      );

      if (isChecked) {
        if (appliedMinutesForRow > 0) {
          const mensaje =
            remainderMinutes > 0
              ? `Banco aplicado: ${formatHHmm(appliedMinutesForRow)} descontado. Resto a pagar: ${formatHHmm(remainderMinutes)}.`
              : `Banco aplicado: ${formatHHmm(appliedMinutesForRow)} descontado. Cobertura completa.`;
          setSnackbarMessage(mensaje);
        } else {
          setSnackbarMessage("Banco insuficiente; se paga íntegro.");
        }
      } else {
        // Revertir consumo si había plan aplicado
        let prevPlan = consumedPlansRef.current[id_marcacion];
        if (prevPlan && prevPlan.length > 0) {
          try {
            const prevPlanWithUser = prevPlan.map((p) => ({ ...p, id_usuario: p.id_usuario || row.id_usuario }));
            for (const p of prevPlanWithUser) {
              await consumirBancoHoras([p], 'revertir');
              setBancoData((prev) =>
                prev.map((b) => {
                  if (b.id !== p.id) return b;
                  const actuales = Number((b.horas ?? b.minutos) || 0);
                  const minRestaurados = actuales + p.minutos;
                  return { ...b, horas: minRestaurados, pagar_fecha: null };
                })
              );
            }
            setSnackbarMessage("Consumo de banco revertido");
            try {
              await saveBankApproval(id_marcacion, 0);
            } catch (e) {
              console.error('Error set aprobacion_bh=0:', e);
            }
          } catch (e) {
            console.error('Error al revertir banco:', e);
            setSnackbarMessage("No se pudo revertir el banco en el servidor");
          } finally {
            delete consumedPlansRef.current[id_marcacion];
            setBankAppliedByRow((prev) => ({ ...prev, [id_marcacion]: 0 }));
          }
        } else {
          setSnackbarMessage("Banco de horas removido");
          setBankAppliedByRow((prev) => ({ ...prev, [id_marcacion]: 0 }));
          try {
            await saveBankApproval(id_marcacion, 0);
          } catch (e) {
            console.error('Error set aprobacion_bh=0:', e);
          }
        }
      }
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error al actualizar pago banco de horas:", error);
      setSnackbarMessage("Error al actualizar pago banco de horas");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setTimeout(() => {
        bankSwitchLocksRef.current[id_marcacion] = false;
        setBankSwitchBusy((prev) => ({ ...prev, [id_marcacion]: false }));
      }, 300);
    }
  };

  // Open modal and set selected row
  const handleOpenModal = async (id_marcacion) => {
    if (modalJustClosed) return;
    const baseRow = filteredData.find((row) => row.id_marcacion === id_marcacion);
    if (!baseRow) return;

    const clonedRow = { ...baseRow };
    clonedRow.desglose_horas = calculateRowDesglose(clonedRow);

    setSelectedRowId(id_marcacion);
    setSelectedRowData(clonedRow);
    setModalOpen(true);

    try {
      const existing = await getOneDatos(id_marcacion);
      if (!existing) {
        return;
      }

      const baseFecha = dayjs(baseRow.fecha || baseRow.prohibido || baseRow.salida || new Date());
      const resolveTime = (timeStr) => {
        if (!timeStr) return null;
        const [h, m] = timeStr.split(":").map((v) => Number(v));
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        const date = (baseFecha.isValid() ? baseFecha : dayjs()).clone();
        return date.hour(h).minute(m).second(0).millisecond(0);
      };

      const enrichedRow = { ...baseRow };

      let entradaCustom = resolveTime(existing.entrada);
      let salidaCustom = resolveTime(existing.salida);
      let descansoInicioCustom = resolveTime(existing.descansos_entrada);
      let descansoFinCustom = resolveTime(existing.descansos_salida);

      if (salidaCustom && entradaCustom && salidaCustom.isBefore(entradaCustom)) {
        salidaCustom = salidaCustom.add(1, "day");
      }

      if (descansoFinCustom && descansoInicioCustom && descansoFinCustom.isBefore(descansoInicioCustom)) {
        descansoFinCustom = descansoFinCustom.add(1, "day");
      }

      if (entradaCustom?.isValid()) {
        enrichedRow.prohibido = entradaCustom.toDate();
      }
      if (salidaCustom?.isValid()) {
        enrichedRow.salida = salidaCustom.toDate();
      }
      if (descansoInicioCustom?.isValid()) {
        enrichedRow.descanso_inicio = descansoInicioCustom.toDate();
      }
      if (descansoFinCustom?.isValid()) {
        enrichedRow.descanso_fin = descansoFinCustom.toDate();
      }

      if (descansoInicioCustom?.isValid() && descansoFinCustom?.isValid()) {
        enrichedRow.minutos_almuerzo = Math.max(0, descansoFinCustom.diff(descansoInicioCustom, "minute"));
      }

      enrichedRow.registroHorasExtras = existing;
      enrichedRow.desglose_horas = calculateRowDesglose(enrichedRow);

      setSelectedRowData(enrichedRow);
    } catch (error) {
      console.error("Error al verificar registro de horas extras:", error);
      setSnackbarMessage("No se pudo verificar el registro de horas extras.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Close modal and blur input to prevent immediate reopen
  const handleCloseModal = () => {
    setModalOpen(false);
    if (selectedRowId && inputRefs.current[selectedRowId]) {
      inputRefs.current[selectedRowId].blur();
    }
    setSelectedRowId(null);
    setSelectedRowData(null);

    setModalJustClosed(true);
    setTimeout(() => setModalJustClosed(false), 300);
  };

  // Update selected overtime type in the row data
  const handleSelectHorasExtras = async (selectedOption) => {
    const row = selectedRowData || filteredData.find((r) => r.id_marcacion === selectedRowId);
    if (!row) {
      return;
    }

    const totalMonto = Number(selectedOption?.montoCalculado || 0);
    const minutosExtras = selectedOption?.resumenCalculo?.minutosExtras ?? 0;
    const horariosConfirmados = selectedOption?.horariosConfirmados || {};

    const idMarcacion = pickNumber(row.id_marcacion);
    const idEmpleado = pickNumber(row.empleado, row.id_empleado, row.empleado_id);
    const idUsuario = pickNumber(row.id_usuario, row.usuario_id, row.usuario);
    const idGrupo = pickNumber(row.id_grupo, row.grupo_id, row.grupo);
    const idSalario = pickNumber(row.id_salario, row.salario_id, row.salario);
    const usuarioApro = pickNumber(secureStorage.getItem("id"), row.usuario_apro, row.id_usuario_apro);

    const entradaStr = horariosConfirmados.entrada
      || (row.prohibido ? dayjs(row.prohibido).format("HH:mm") : "");
    const salidaStr = horariosConfirmados.salida
      || (row.salida ? dayjs(row.salida).format("HH:mm") : "");
    const descansoInicioStr = horariosConfirmados.descansoInicio
      || (row.descanso_inicio ? dayjs(row.descanso_inicio).format("HH:mm") : "");
    const descansoFinStr = horariosConfirmados.descansoFin
      || (row.descanso_fin ? dayjs(row.descanso_fin).format("HH:mm") : "");

    if ([idMarcacion, idEmpleado, idUsuario, idGrupo, idSalario, usuarioApro].some((value) => value === null)) {
      setSnackbarMessage("Faltan datos para registrar las horas extras.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      await saveOvertimeActivation(idMarcacion, totalMonto, 1);

      const fechaDescripcion = row.fecha ? dayjs(row.fecha).format("DD/MM/YYYY") : "";
      const payload = {
        id_empleado: idEmpleado,
        id_usuario: idUsuario,
        id_grupo: idGrupo,
        horas: formatMinutesToHHmm(minutosExtras),
        id_salario: idSalario,
        entrada: entradaStr,
        salida: salidaStr,
        descripcion: fechaDescripcion
          ? `Horas extras ${fechaDescripcion}`
          : "Horas extras",
        usuario_apro: usuarioApro,
        obs: " ",
        id_marcacion: idMarcacion,
        descansos_entrada: descansoInicioStr || "",
        descansos_salida: descansoFinStr || "",
      };

      await PostDatosHoras(payload);

      setRowSettings((prev) => ({
        ...prev,
        [idMarcacion]: { enabled: true },
      }));
      setBankSettings((prev) => ({
        ...prev,
        [idMarcacion]: false,
      }));
      setFilteredData((prev) =>
        prev.map((current) =>
          current.id_marcacion === idMarcacion
            ? {
                ...current,
                descripcion_horas_extras: totalMonto,
                aprobación_horas_extras: 1,
              }
            : current
        )
      );
      setData((prev) =>
        prev.map((current) =>
          current.id_marcacion === idMarcacion
            ? {
                ...current,
                descripcion_horas_extras: totalMonto,
                aprobación_horas_extras: 1,
              }
            : current
        )
      );
      setSnackbarMessage("Horas extras registradas y activadas.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleCloseModal();
    } catch (error) {
      console.error("Error al confirmar horas extras:", error);
      setSnackbarMessage("Error al confirmar horas extras.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  
  const columns = [
    { field: "id_marcacion", headerName: "ID", flex: 0.5 },
    { field: "empleado", headerName: "ID Empleado", flex: 1 },
    { field: "nombre", headerName: "Nombre", flex: 1 },
    { field: "grupo", headerName: "Grupo", flex: 1 },
    {
      field: "horas",
      headerName: "Horas Trabajadas",
      flex: 1,
      valueGetter: (params) => {
        return params.row.horas || 0;
      },
      valueFormatter: (params) => {
        const minutos = params.value;
        const horas = Math.floor(minutos / 60);
        const minutosRestantes = minutos % 60;
        return `${horas.toString().padStart(2, '0')}:${minutosRestantes.toString().padStart(2, '0')}`;
      },
    },
    {
      field: "desglose_horas",
      headerName: "Desglose de Horas",
      flex: 1.5,
      valueGetter: (params) => calculateRowDesglose(params.row),
      renderCell: (params) => {
        const valor = params.value;
        const formatMinutos = (minutos) => {
          const horas = Math.floor(minutos / 60);
          const mins = Math.round(minutos % 60);
          return `${horas}:${mins.toString().padStart(2, '0')}`;
        };

        if (!valor || !valor.desglose) {
          return (
            <Typography variant="body2" style={{ color: colors.grey[300] }}>
              —
            </Typography>
          );
        }

        if (valor.tipo === "Domingo/Feriado") {
          return (
            <Box>
              <Typography variant="body2" style={{ color: colors.grey[100] }}>
                Domingo/Feriado (+100%)
              </Typography>
              <Typography variant="caption" style={{ color: colors.greenAccent[400] }}>
                {formatMinutos(valor.desglose.domingo)} horas
              </Typography>
            </Box>
          );
        }

        return (
          <Box>
            {valor.desglose.diurnas > 0 && (
              <Typography variant="body2" style={{ color: colors.grey[100] }}>
                Diurnas (+50%): {formatMinutos(valor.desglose.diurnas)}
              </Typography>
            )}
            {valor.desglose.nocturnas > 0 && (
              <Typography variant="body2" style={{ color: colors.greenAccent[400] }}>
                Nocturnas (+130%): {formatMinutos(valor.desglose.nocturnas)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "prohibido",
      headerName: "Entrada",
      flex: 1,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), "HH:mm:ss") : "",
    },
    {
      field: "salida",
      headerName: "Salida",
      flex: 1,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), "HH:mm:ss") : "",
    },
    {
      field: "fecha",
      headerName: "Fecha",
      flex: 1,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), "dd/MM/yyyy") : "",
    },
    {
      field: "descripcion_horas_extras",
      headerName: "Valor de Horas Extras",
      flex: 1,
      renderCell: (params) => {
        const id = params.row.id_marcacion;
        const value = params.value ? `${Number(params.value).toLocaleString('es-PY')} ₲s` : "";
      
        return (
          <TextField
            variant="standard"
            value={value}
            onClick={() => {
              if (!modalJustClosed) {
                handleOpenModal(id);
              }
            }}
            InputProps={{
              readOnly: true,
              sx: { cursor: "pointer" },
            }}
            fullWidth
            inputRef={(el) => (inputRefs.current[id] = el)}
          />
        );
      },
    },
    {
      field: "banco_aplicado",
      headerName: "Banco aplicado",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const id = params.row.id_marcacion;
        const min = bankAppliedByRow[id] || 0;
        return (
          <Typography variant="body2" color={min > 0 ? colors.greenAccent[400] : colors.grey[300]}>
            {min > 0 ? formatHHmm(min) : '—'}
          </Typography>
        );
      },
    },
    {
      field: "approve_switch",
      headerName: "Activo para Pago",
      flex: 0.7,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const id = params.row.id_marcacion;
        const enabled = rowSettings[id]?.enabled || false;
        const bankOn = !!bankSettings[id];
        return (
          <Switch
            checked={enabled}
            onChange={handleSwitchChange(id)}
            color="primary"
            disabled={bankOn}
          />
        );
      },
    },
    {
      field: "bank_switch",
      headerName: "Pago Banco de Hora",
      flex: 0.9,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const id = params.row.id_marcacion;
        const enabled = bankSettings[id] || false;
        const busy = !!bankSwitchBusy[id];
        // Mostrar solo si el funcionario tiene banco de horas (> 0 considerando el factor domingo)
        const horasBanco = getBankHoursForRow(params.row);
        // Si ya está activado (aprobacion_bh=1), mostramos el switch aunque no haya saldo visible
        if (!enabled && (!horasBanco || horasBanco <= 0)) {
          return <Typography variant="caption" color={colors.grey[300]}>—</Typography>;
        }
        return (
          <Switch
            checked={enabled}
            onChange={handleBankSwitchChange(id)}
            color="secondary"
            disabled={busy}
          />
        );
      },
    },
  ];

  return (
    <Box m="20px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={0}
        mb={2}
      >
        <Header
          title="Registros de Horas Extras"
          subtitle="Horas Extras Habilitadas"
        />

        <Box display="flex" alignItems="center" gap={2}>
          <Box
            display="flex"
            backgroundColor={colors.primary[400]}
            borderRadius="3px"
            height="40px"
            width="300px"
            alignItems="center"
            px={1}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Buscar"
              value={searchValue}
              onChange={handleSearchChange}
            />
            <IconButton type="button" sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={2}>
              <DatePicker
                label="Fecha de Inicio"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="Fecha de Fin"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </Box>

      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={filteredData}
          getRowId={(row) => row.id_marcacion}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          disableMultipleSelection={true}
        />
      </Box>

      <ModalHorasExtras
        open={modalOpen}
        onClose={handleCloseModal}
        onSelectGrupo={handleSelectHorasExtras}
        selectedRowId={selectedRowId}
        selectedRowData={selectedRowData}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HorasExtras;
